import { Box, Divider, Grid, Paper, Typography } from "@mui/material";
import { useMemo } from "react";
import { CustomColumn } from "../..";
import FilterChip from "../FilterChip";
import SingleDateFilter from "./SingleDateFilter";
import SingleRadio from "./SingleRadio/index";
import SingleRangeFilter from "./SingleRangeFilter";
import SingleTextAc from "./SingleTextAc";
import { FilterTableInfo } from "./types";
import { getStatusApiValue, StatusOptions } from "./utils";
import isObjectEmpty from "helpers/isObjectEmpty";

interface FilterBodyProps<T> {
  filters: Record<string, any>;
  setFilters: (filters: Record<string, any>) => void;
  initialData: T[];
  clearFilters: () => void;
  filteredCustomColumns: CustomColumn;
  countriesList: any[];
  statusOptions: StatusOptions;
  getStatusDisplayValue: (statusKey: keyof StatusOptions, apiValue: string) => string;
  tableInfo: FilterTableInfo;
  headers: string[];
}

const FilterBody = <T extends Record<string, any>>({
  filters,
  setFilters,
  initialData,
  clearFilters,
  filteredCustomColumns,
  countriesList,
  statusOptions,
  getStatusDisplayValue,
  tableInfo,
  headers
}: FilterBodyProps<T>) => {
  const { isMapDetails, isUserManagementDetails } = tableInfo;
  const customColumnsExist = filteredCustomColumns && !isObjectEmpty(filteredCustomColumns);

  const { textHeaders, numberHeaders, dateHeaders, boolHeaders } = useMemo(() => {
    let textHeaders: string[] = [];
    let numberHeaders: Record<string, any>[] = [];
    let dateHeaders: Record<string, any>[] = [];
    let boolHeaders: string[] = [];

    if (customColumnsExist) {
      textHeaders = Object.entries(filteredCustomColumns)
        .filter(
          ([key, value]) =>
            (value.type === undefined || value.type === "text") && 
            !value.noFilter &&
            headers.includes(key)
        )
        .map(([key]) => key);

      const unTyped = headers.filter((header) => {
        if (textHeaders.includes(header)) return false;
        if (
          (header === "status" ||
            header.endsWith("_status") ||
            header === "internalStatus" ||
            header === "internal_status" ||
            header === "departments" ||
            header === "is_active") &&
          Object.keys(statusOptions).includes(header)
        ) {
          return false;
        }
        return (
          !Object.keys(filteredCustomColumns).includes(header) ||
          (filteredCustomColumns[header]?.type === undefined &&
            !filteredCustomColumns[header]?.noFilter)
        );
      });

      textHeaders = [...textHeaders, ...unTyped];

      boolHeaders = Object.entries(filteredCustomColumns)
        .filter(
          ([key, value]) => 
            value.type === "boolean" && 
            !value.noFilter &&
            headers.includes(key)
        )
        .map(([key]) => key);

      numberHeaders = Object.entries(filteredCustomColumns)
        .filter(
          ([key, value]) => 
            value.type === "number" && 
            !value.noFilter &&
            headers.includes(key)
        )
        .map(([key]) => ({ name: key }));

      dateHeaders = Object.entries(filteredCustomColumns)
        .filter(
          ([key, value]) => 
            value.type === "date" && 
            !value.noFilter &&
            headers.includes(key)
        )
        .map(([key]) => {
          const dateRange = filters[key]?.keywords;
          if (dateRange) {
            const formattedDateRange = dateRange;
            return { name: key, formattedDateRange };
          }
          return { name: key };
        });
    } else {
      textHeaders = headers;
    }

    return { textHeaders, numberHeaders, dateHeaders, boolHeaders };
  }, [customColumnsExist, filteredCustomColumns, headers, filters, statusOptions]);

  const hasTextFilters = textHeaders.length > 0;
  const hasBoolFilters = boolHeaders.length > 0;
  const hasNumberFilters = numberHeaders.length > 0;
  const hasDateFilters = dateHeaders.length > 0;

  // Filter section rendering helper
  const renderFilterSection = (title: string, content: React.ReactNode) => (
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          fontWeight: 600, 
          mb: 1,
          fontSize: "1rem",
          color: "#2c3e50"
        }}
      >
        {title}
      </Typography>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          borderRadius: 1, 
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef"
        }}
      >
        {content}
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", px: 1 }}>
      {!isObjectEmpty(filters) && (
        <Box sx={{ mb: 2 }}>
          <FilterChip
            filters={filters}
            clearFilters={clearFilters}
            customColumns={filteredCustomColumns}
            getStatusDisplayValue={getStatusDisplayValue}
          />
          <Divider sx={{ my: 1.5 }} />
        </Box>
      )}

      {hasTextFilters && (
        renderFilterSection("Text Filters", (
          <Grid container spacing={2}>
            {textHeaders.map((header) => {
              if (
                (header === "buyer_country") &&
                countriesList.length > 0
              ) {
                return (
                  <Grid item xs={12} sm={6} key={header}>
                    <SingleTextAc
                      header={header}
                      data={countriesList.map((country) => country.name)}
                      filters={filters}
                      setFilters={setFilters}
                      customFieldName={
                        customColumnsExist
                          ? filteredCustomColumns?.[header]?.[header]
                          : "Buyer Country"
                      }
                    />
                  </Grid>
                );
              }
              if (
                (header === "country" ||
                  header === "name" ||
                  header === "buyer_country" ||
                  header === "seller_country" ||
                  header === "buyerCountry") &&
                countriesList.length > 0
              ) {
                return (
                  <Grid item xs={12} sm={6} key={header}>
                    <SingleTextAc
                      header={header}
                      data={countriesList.map((country) => country.name)}
                      filters={filters}
                      setFilters={setFilters}
                      customFieldName={
                        customColumnsExist
                          ? filteredCustomColumns?.[header]?.[header]
                          : ""
                      }
                    />
                  </Grid>
                );
              }

              if (header === "director") {
                return (
                  <Grid item xs={12} sm={6} key={header}>
                    <SingleTextAc
                      header={header}
                      data={[
                        ...new Set(initialData.map((option) => option[header])),
                      ]}
                      filters={filters}
                      setFilters={setFilters}
                      customFieldName={
                        customColumnsExist
                          ? filteredCustomColumns?.[header]?.[header]
                          : "Director"
                      }
                    />
                  </Grid>
                );
              }

              if (
                header === "status" ||
                header.endsWith("_status") ||
                header === "internalStatus" ||
                header === "internal_status" ||
                header === "departments" ||
                header === "is_active" ||
                header === "approval" ||
                header === "transaction_type" ||
                header === "currency"
              ) {
                const statusMapping = statusOptions[header as keyof typeof statusOptions];

                if (statusMapping) {
                  return (
                    <Grid item xs={12} sm={6} key={header}>
                      <SingleTextAc
                        header={header}
                        data={Object.keys(statusMapping)}
                        filters={filters}
                        setFilters={setFilters}
                        customFieldName={
                          customColumnsExist
                            ? filteredCustomColumns?.[header]?.[header]
                            : ""
                        }
                        getStatusApiValue={(value: string) =>
                          getStatusApiValue(statusOptions, header, value)
                        }
                      />
                    </Grid>
                  );
                }
              }

              if (isMapDetails && header === "product") {
                const productOptions = {
                  Factoring: "factoring",
                  "Reverse Factoring": "reverse_factoring",
                };
                return (
                  <Grid item xs={12} sm={6} key={header}>
                    <SingleTextAc
                      header={header}
                      data={Object.keys(productOptions)}
                      filters={filters}
                      setFilters={setFilters}
                      customFieldName={
                        customColumnsExist
                          ? filteredCustomColumns?.[header]?.[header]
                          : ""
                      }
                      getStatusApiValue={(value: string) =>
                        productOptions[value as keyof typeof productOptions] || value
                      }
                    />
                  </Grid>
                );
              }

              // Handle product filter for termsheet tables as well
              if (header === "product" && statusOptions.product) {
                return (
                  <Grid item xs={12} sm={6} key={header}>
                    <SingleTextAc
                      header={header}
                      data={Object.keys(statusOptions.product)}
                      filters={filters}
                      setFilters={setFilters}
                      customFieldName={
                        customColumnsExist
                          ? filteredCustomColumns?.[header]?.[header]
                          : ""
                      }
                      getStatusApiValue={(value: string) =>
                        getStatusApiValue(statusOptions, header, value)
                      }
                    />
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} sm={6} key={header}>
                  <SingleTextAc
                    header={header}
                    data={[
                      ...new Set(initialData.map((option) => option[header])),
                    ]}
                    filters={filters}
                    setFilters={setFilters}
                    customFieldName={
                      customColumnsExist
                        ? filteredCustomColumns?.[header]?.[header]
                        : ""
                    }
                  />
                </Grid>
              );
            })}
          </Grid>
        ))
      )}

      {hasBoolFilters && (
        renderFilterSection("Boolean Filters", (
          <Grid container spacing={2}>
            {boolHeaders.map((header) => (
              <Grid item xs={12} sm={6} key={header}>
                <SingleRadio
                  header={header}
                  data={initialData}
                  filters={filters}
                  setFilters={setFilters}
                  boolTitles={["True", "False"]}
                  customFieldName={
                    customColumnsExist
                      ? filteredCustomColumns?.[header]?.[header] ?? "Active"
                      : "Active"
                  }
                />
              </Grid>
            ))}
          </Grid>
        ))
      )}

      {hasNumberFilters && (
        renderFilterSection("Number Filters", (
          <Grid container spacing={2}>
            {numberHeaders.map((header) => {
              const max = Math.max(
                ...initialData.map((item) =>
                  Number(item[header.name])
                )
              );
              const min = Math.min(
                ...initialData.map((item) =>
                  Number(item[header.name])
                )
              );

              const formattedMax = isFinite(max) ? max : 100;
              const formattedMin = isFinite(min) ? min : 0;

              return (
                <Grid item xs={12} key={header.name}>
                  <SingleRangeFilter
                    range={[formattedMin, formattedMax]}
                    label={header.name}
                    filters={filters}
                    setFilters={setFilters}
                    customColumns={filteredCustomColumns}
                  />
                </Grid>
              );
            })}
          </Grid>
        ))
      )}

      {hasDateFilters && (
        renderFilterSection("Date Filters", (
          <Grid container spacing={2}>
            {dateHeaders.map((header) => {
              const dates = initialData
                .map((item) => item[header.name])
                .filter((date) => date);

              const standardize = (val: string) =>
                new Date(val);

              let max: Date;
              let min: Date;

              if (dates.length > 0) {
                max = new Date(
                  Math.max(
                    ...dates.map((date) => standardize(date).getTime())
                  )
                );
                min = new Date(
                  Math.min(
                    ...dates.map((date) => standardize(date).getTime())
                  )
                );
              } else {
                max = new Date();
                min = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
              }

              return (
                <Grid item xs={12} key={header.name}>
                  <SingleDateFilter
                    range={[min, max]}
                    label={header.name}
                    filters={filters}
                    setFilters={setFilters}
                    customColumns={filteredCustomColumns}
                    disabled={!isFinite(min.getTime()) || !isFinite(max.getTime())}
                  />
                </Grid>
              );
            })}
          </Grid>
        ))
      )}
    </Box>
  );
};

export default FilterBody;
